/*
 * <product-extras>
 * The cart drawer's Add Extras offers on the product page (sections/b22-product-extras).
 * Same markup and add flow as <cart-extras> (cart-extras.js), so it extends that
 * element; on top it opens the cart drawer after an add, and re-renders itself from
 * this page's section rather than the drawer's.
 *
 * A successful add is detected from the cartUpdate event cart-extras.js publishes
 * (source 'cart-extras'), so nothing in cart-extras.js had to change.
 */
customElements.whenDefined('cart-extras').then(() => {
  if (customElements.get('product-extras')) return;
  const CartExtras = customElements.get('cart-extras');

  customElements.define(
    'product-extras',
    class ProductExtras extends CartExtras {
      connectedCallback() {
        super.connectedCallback();
        this.watchPurchaseOption();
        // cart-extras.js publishes source 'cart-extras' for adds from this section and
        // from the drawer; the inherited subscription ignores that source, so refresh
        // here, and open the drawer only for this section's own add.
        this.addedUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (!event || event.source !== 'cart-extras') return;
          if (this.pendingButton) {
            this.openCart(this.pendingButton);
            this.pendingButton = null;
          }
          this.refresh();
        });
        // A new size (Box of 4 / Box of 6): product-info.js has just rendered the
        // section for it, so take this block's copy from that ("This item" photo and
        // price, and the 20% worked out against the chosen box).
        this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const html = event && event.data && event.data.html;
          if (!html || typeof html.getElementById !== 'function') return;
          this.swapContents(html);
        });
      }

      disconnectedCallback() {
        super.disconnectedCallback();
        if (this.addedUnsubscriber) this.addedUnsubscriber();
        if (this.variantUnsubscriber) this.variantUnsubscriber();
        document.removeEventListener('change', this.syncPurchaseOption);
        document.removeEventListener('click', this.syncPurchaseOptionSoon);
      }

      // The 20% second box is for one-off boxes only. When "Subscribe" is chosen on
      // this page, prices that relied on this page's box show in full (CSS keyed on
      // product-extras--subscription + [data-discount-one-time-only]).
      watchPurchaseOption() {
        this.syncPurchaseOption = () => {
          const form = document.getElementById(this.dataset.renderSection);
          if (!form || form.tagName !== 'FORM') return;
          this.classList.toggle('product-extras--subscription', Boolean(new FormData(form).get('selling_plan')));
        };
        this.syncPurchaseOptionSoon = () => setTimeout(this.syncPurchaseOption, 60);
        document.addEventListener('change', this.syncPurchaseOption);
        document.addEventListener('click', this.syncPurchaseOptionSoon);
        this.syncPurchaseOption();
      }

      // "+" adds the extra TOGETHER with the product on this page (the 20% second box
      // only exists alongside it), unless that product is already in the basket.
      addVariant(button, properties) {
        if (this.addingWithProduct) return Promise.resolve(false);
        this.addingWithProduct = true;
        this.pendingButton = button;
        button.setAttribute('aria-busy', 'true');
        return this.addCurrentProduct()
          .then(() => super.addVariant(button, properties))
          .finally(() => {
            this.addingWithProduct = false;
            button.removeAttribute('aria-busy');
          });
      }

      // Adds this page's product exactly as its own Add to basket would (variant,
      // quantity, subscription plan, properties), by posting the main product form.
      // Skipped when it is already in the basket, sold out, or a card whose required
      // message is still empty; the extra is still added on its own.
      addCurrentProduct() {
        const form = document.getElementById(this.dataset.renderSection);
        if (!form || form.tagName !== 'FORM') return Promise.resolve(false);
        const submit = form.querySelector('[type="submit"]');
        if (submit && (submit.disabled || submit.getAttribute('aria-disabled') === 'true')) return Promise.resolve(false);
        const required = form.querySelector('[data-required-property]') || document.querySelector(`[data-required-property][form="${form.id}"]`);
        if (required && required.value.trim() === '') return Promise.resolve(false);
        const variantId = Number(new FormData(form).get('id'));

        return fetch(`${this.cartUrl}.js`)
          .then((response) => response.json())
          .then((cart) => {
            if (cart.items.some((item) => item.variant_id === variantId)) return false;
            const config = fetchConfig('javascript');
            config.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete config.headers['Content-Type'];
            config.body = new FormData(form);
            // A size sold one-off only (Box of 4, b22-variant-panels.js) never goes with a plan.
            if (document.querySelector(`[data-b22-one-time-variants*=",${variantId},"]`)) config.body.delete('selling_plan');
            return fetch(`${routes.cart_add_url}`, config)
              .then((response) => response.json())
              .then((response) => !response.status);
          })
          .catch((error) => {
            console.error(error);
            return false;
          });
      }

      // Rendered for the size chosen on this page, not the product's default.
      refresh() {
        const params = new URLSearchParams(window.location.search);
        params.set('section_id', this.dataset.renderSection || this.dataset.sectionId);
        const form = document.getElementById(this.dataset.renderSection);
        const variantId = form && form.tagName === 'FORM' ? new FormData(form).get('id') : null;
        if (variantId) params.set('variant', variantId);
        fetch(`${window.location.pathname}?${params}`)
          .then((response) => response.text())
          .then((html) => this.swapContents(new DOMParser().parseFromString(html, 'text/html')))
          .catch((error) => console.error(error));
      }

      // Replaces the offers with this block's copy from a freshly rendered section,
      // keeping the tab the customer was on. The gift icon has already swung once.
      swapContents(doc) {
        const fresh = doc.getElementById(this.id);
        const contents = fresh && fresh.querySelector('.js-cart-extras-contents');
        if (!contents) return;
        const activeTab = this.querySelector('[data-tab-button].active');
        const activeTabId = activeTab ? activeTab.dataset.tabButton : null;
        this.innerHTML = contents.innerHTML;
        this.classList.add('product-extras--refreshed');
        this.buildFilters();
        if (!activeTabId) return;
        const tab = this.querySelector(`[data-tab-button="${activeTabId}"]`);
        if (tab && !tab.classList.contains('active')) tab.click();
      }

      // As product-form.js does after "Add to basket".
      openCart(button) {
        const drawer = document.getElementById('cart-drawer');
        const opener = document.getElementById('cart-drawer-opener');
        if (!drawer || !opener) return;
        const cartItems = document.querySelector('cart-items');
        if (cartItems && cartItems.hideEmptyCart) cartItems.hideEmptyCart();
        opener.openDrawer('cart-drawer');
        if (drawer.setActiveElement) drawer.setActiveElement(button);
      }
    }
  );
});
